import './period-selector.component.ajscss';

import { SessionDeprecatedService } from '@commons/core/auth/session-deprecated.service';
import { hasChange } from '@commons/util/helper/helper';
import { copy, IChangesObject, IComponentController, IComponentOptions, isDefined } from 'angular';
import { i18n } from 'i18next';

import templateUrl from './period-selector.component.ajs.html';

const MAX_HOUR: number = 23;
const MAX_MINUTE: number = 59;
class PeriodSelectorCtrl implements IComponentController {
  public isPreciseTimeSupported: Boolean;
  public isCurrentAsMax: Boolean;
  public format: string;
  public minuteStep: number;
  public minTime: Date | null;
  public maxTime: Date | null;
  public periodChanged: Function;
  public fromLabelText: string;
  public toLabelText: string;
  public isPeriodFilterEnabled: Boolean;
  public beginDateTime: Date;
  public endDateTime: Date;
  private beginHour: number;
  private beginMinute: number;
  private endHour: number;
  private endMinute: number;
  public maxDateTime: Date | null;
  private maxBeginHour: number;
  private maxBeginMinute: number;
  private maxEndHour: number;
  private maxEndMinute: number;

  constructor(private readonly sessionDeprecatedService: SessionDeprecatedService, private readonly $i18next: i18n) {}

  /**
   * Initiation for properties without values passed from parent component
   */
  $onInit(): void {
    this.isPreciseTimeSupported = isDefined(this.isPreciseTimeSupported) ? this.isPreciseTimeSupported : false;
    this.isPeriodFilterEnabled = isDefined(this.isPeriodFilterEnabled) ? this.isPeriodFilterEnabled : false;
    this.beginDateTime = isDefined(this.beginDateTime) ? this.beginDateTime : new Date();
    this.endDateTime = isDefined(this.endDateTime) ? this.endDateTime : new Date();
    this.minuteStep = this.minuteStep || 1;
    this.format = this.format || 'DD/MMM/YYYY';
    this.fromLabelText = this.fromLabelText || this.$i18next.t('commons:application.period.from');
    this.toLabelText = this.toLabelText || this.$i18next.t('commons:application.period.to');
    if (isDefined(this.minTime) && isDefined(this.maxTime)) {
      if (this.minTime && this.maxTime && this.minTime > this.maxTime) {
        this.minTime = this.maxTime = null;
      }
    }
    this.maxDateTime = new Date();
    this.maxBeginHour = this.maxDateTime.getHours();
    this.maxBeginMinute = this.maxDateTime.getMinutes();
    this.maxEndHour = this.maxDateTime.getHours();
    this.maxEndMinute = this.maxDateTime.getMinutes();
    this.maxTime = this.maxTime || this.isCurrentAsMax ? this.getCurrentDateMaxTime() : null;
    this.initCurrentTime();
  }

  /**
   * The status of boolean isPeriodFilterEnabled is what we care about:
   * when filter becomes disabled, time delta should be 0
   * when filter becomes enabled, and minutes and hours can't be set by users , we initiate again these two
   * @param changesObj
   */
  $onChanges(changesObj: { [property: string]: IChangesObject }) {
    if (hasChange(changesObj['isPeriodFilterEnabled'])) {
      this.isPeriodFilterEnabled = copy(changesObj['isPeriodFilterEnabled'].currentValue);
      if (!this.isPeriodFilterEnabled) {
        this.beginDateTime = new Date();
        this.endDateTime = new Date();
        this.beginHour = this.beginDateTime.getHours();
        this.beginMinute = this.beginDateTime.getMinutes();
        this.endHour = this.endDateTime.getHours();
        this.endMinute = this.endDateTime.getMinutes();
        this.buildBeginTime();
        this.buildEndTime();
      } else {
        if (!this.isPreciseTimeSupported) {
          this.beginHour = this.beginMinute = 0;
          this.endHour = 23;
          this.endMinute = 59;
          this.updateBeginTime();
          this.updateEndTime();
        }
      }
    }
  }

  /**
   * While this.beginHour is in two-way data binding, we should update it directly since the begin time building process will
   * never wait till it is well updated by angular from its children side
   * @param beginHour
   */
  public updateBeginHour(beginHour) {
    this.beginHour = beginHour;
    this.updateBeginTime();
  }

  /**
   * While this.beginMinute is in two-way data binding, we should update it directly since the begin time building process will
   * never wait till it is well updated by angular from its children side
   * @param beginMinute
   */
  public updateBeginMinute(beginMinute) {
    this.beginMinute = beginMinute;
    this.updateBeginTime();
  }

  /**
   * Update the changed begin time bind to child directive and notify it to parent component
   */
  private updateBeginTime() {
    this.buildBeginTime();
    this.changePeriod();
  }

  /**
   * While this.endHour is in two-way data binding, we should update it directly since the end time building process will
   * never wait till it is well updated by angular from its children side
   * @param endHour
   */
  public updateEndHour(endHour) {
    this.endHour = endHour;
    this.updateEndTime();
  }

  /**
   * While this.endMinute is in two-way data binding, we should update it directly since the end time building process will
   * never wait till it is well updated by angular from its children side
   * @param endMinute
   */
  public updateEndMinute(endMinute) {
    this.endMinute = endMinute;
    this.updateEndTime();
  }

  /**
   * Update the changed end time bind to child directive and notify it to parent component
   */
  private updateEndTime() {
    this.buildEndTime();
    this.changePeriod();
  }

  /**
   * Notify the new changes to parent
   */
  public changePeriod() {
    this.periodChanged({ beginTime: this.beginDateTime, endTime: this.endDateTime });
    this.checkDate();
  }

  /**
   * Get the local language
   */
  public getLanguage(): string {
    return this.sessionDeprecatedService.getProfile().language;
  }

  private checkDate() {
    if (this.maxDateTime) {
      if (this.isSameDate(this.beginDateTime, this.maxDateTime)) {
        this.maxBeginHour = this.maxDateTime.getHours();
        this.maxBeginMinute = this.beginHour == this.maxDateTime.getHours() ? this.maxDateTime.getMinutes() : MAX_MINUTE;
      } else {
        this.maxBeginHour = MAX_HOUR;
        this.maxBeginMinute = MAX_MINUTE;
      }
      if (this.isSameDate(this.endDateTime, this.maxDateTime)) {
        this.maxEndHour = this.maxDateTime.getHours();
        this.maxEndMinute = this.endHour == this.maxDateTime.getHours() ? this.maxDateTime.getMinutes() : MAX_MINUTE;
      } else {
        this.maxEndHour = MAX_HOUR;
        this.maxEndMinute = MAX_MINUTE;
      }
    }
    if (this.isSameDate(this.beginDateTime, this.endDateTime)) {
      this.maxBeginHour = this.endHour;
      this.maxBeginMinute = this.beginHour == this.endHour ? this.endMinute : MAX_MINUTE;
    } else {
      this.maxBeginHour = MAX_HOUR;
      this.maxBeginMinute = MAX_MINUTE;
    }
  }
  /**
   * Initiate minutes and hours in different case:
   *  users can set precise time themselves: show current time;
   *  users are not allowed to select precise time, we consider the time delta as the max scope
   */
  private initCurrentTime() {
    if (this.isPreciseTimeSupported) {
      this.beginHour = this.beginDateTime.getHours();
      this.beginMinute = this.beginDateTime.getMinutes();
      this.endHour = this.endDateTime.getHours();
      this.endMinute = this.endDateTime.getMinutes();
    } else {
      this.beginHour = 0;
      this.beginMinute = 0;
      this.endHour = 23;
      this.endMinute = 59;
    }
    this.buildBeginTime();
    this.buildEndTime();
  }

  /**
   * Test if dateA and dateB are the same
   */
  private isSameDate(dateA: Date, dateB: Date): boolean {
    return dateA.getDate() == dateB.getDate() && dateA.getMonth() == dateB.getMonth() && dateA.getFullYear() == dateB.getFullYear();
  }
  /**
   * To provide the max time of the date(considering current time when minutes and hours are free to be set)
   */
  private getCurrentDateMaxTime() {
    let currentDate = new Date();
    currentDate.setHours(23);
    currentDate.setMinutes(59);
    return currentDate;
  }

  /**
   * Build begin time with minutes and hours and compare it with min time
   */
  private buildBeginTime() {
    let newBeginTime = copy(this.beginDateTime);
    newBeginTime.setHours(this.beginHour);
    newBeginTime.setMinutes(this.beginMinute);
    if (this.isPreciseTimeSupported && this.minTime && newBeginTime < this.minTime) {
      // only in mode minutes-hours should we compare the min time and current input time
      newBeginTime = copy(this.minTime);
      this.beginMinute = this.minTime.getMinutes();
      this.beginHour = this.minTime.getHours();
    }
    this.beginDateTime = newBeginTime;
  }

  /**
   * Build end time with minutes and hours and compare it with max time
   */
  private buildEndTime() {
    let newEndTime = copy(this.endDateTime);
    newEndTime.setHours(this.endHour);
    newEndTime.setMinutes(this.endMinute);
    if (this.isPreciseTimeSupported && this.maxTime && newEndTime > this.maxTime) {
      // only in mode minutes-hours should we compare the max time and current input time
      newEndTime = copy(this.maxTime);
      this.endMinute = this.maxTime.getMinutes();
      this.endHour = this.maxTime.getHours();
    }
    this.endDateTime = newEndTime;
  }
}

export class PeriodSelector implements IComponentOptions {
  templateUrl = templateUrl;
  bindings = {
    isPreciseTimeSupported: '<',
    isCurrentAsMax: '<',
    isPeriodFilterEnabled: '<',
    minuteStep: '<',
    minTime: '<',
    maxTime: '<',
    format: '@',
    fromLabelText: '@',
    toLabelText: '@',
    periodChanged: '&',
    beginDateTime: '=?',
    endDateTime: '=?',
  };
  controller = ['sessionDeprecatedService', '$i18next', PeriodSelectorCtrl];
}

export default 'periodSelector';
